import { Component, inject } from '@angular/core';
import { ToastService, type ToastVariant } from './toast.service';

/**
 * Fixed, top-right stack that renders the {@link ToastService} queue using
 * plain Bootstrap toast markup (no ng-bootstrap dependency). Mount once, near
 * the app root.
 *
 * Accessibility: errors are announced assertively (role=alert / aria-live
 * assertive); success/info announce politely (role=status) so they don't
 * interrupt a screen-reader user mid-sentence.
 */
@Component({
	selector: 'app-toast-container',
	standalone: true,
	host: { class: 'toast-container position-fixed top-0 end-0 p-3', style: 'z-index: 1200' },
	template: `
		@for (toast of toastService.toasts(); track toast.id) {
			<div
				class="toast show align-items-center text-bg-{{ colorClass(toast.variant) }} border-0 mb-2"
				[attr.role]="toast.variant === 'error' ? 'alert' : 'status'"
				[attr.aria-live]="toast.variant === 'error' ? 'assertive' : 'polite'"
				aria-atomic="true"
			>
				<div class="d-flex">
					<div class="toast-body">{{ toast.message }}</div>
					<button
						type="button"
						class="btn-close btn-close-white me-2 m-auto"
						aria-label="Close"
						(click)="toastService.remove(toast.id)"
					></button>
				</div>
			</div>
		}
	`,
})
export class ToastContainerComponent {
	protected readonly toastService = inject(ToastService);

	/** Map a toast variant to its Bootstrap contextual color token. */
	protected colorClass(variant: ToastVariant): string {
		if (variant === 'error') return 'danger';
		if (variant === 'success') return 'success';
		return 'primary';
	}
}
